import { useEffect, useRef } from 'react';
import { useSeason } from '@/lib/season';
import { useScheme } from '@/lib/theme';
import type { Scene } from './scene';

const TAPPABLE = 'button, a[href], [role="button"], [role="radio"], [role="tab"]';

const prefersReduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Сезонный слой: canvas поверх страницы, но под меню и шторками (z-40 против z-50
 * и z-[60]). Под содержимым его почти не было видно: экран сплошь из карточек, и
 * листья мелькали только в щелях. Частицы редкие и полупрозрачные, клики проходят
 * насквозь, экранным читалкам слой не виден. Нет сезона: ничего не рисуется и не грузится.
 */
export function SeasonBackdrop() {
  const season = useSeason();
  const scheme = useScheme();
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!season || !ref.current) return;
    let scene: Scene | null = null;
    let cancelled = false;
    let lastBurst = 0;
    const canvas = ref.current;
    void import('./scene').then(({ startScene, isLowEnd }) => {
      if (cancelled) return;
      scene = startScene(canvas, season, scheme, prefersReduced() || isLowEnd());
    });
    // Вылет частиц из-под пальца на любой кнопке и ссылке. По click, а не pointerdown:
    // начало прокрутки и второй палец не должны сыпать листьями. Нажатие с клавиатуры
    // (detail 0, координат нет) пропускаем. Ничего не отменяем, частицы только рисуются.
    const onDown = (e: MouseEvent) => {
      if (!scene || e.button !== 0 || e.detail === 0) return;
      const el = (e.target as Element | null)?.closest?.(TAPPABLE);
      if (!el || el.matches(':disabled, [aria-disabled="true"]')) return;
      const now = performance.now();
      if (now - lastBurst < 120) return;
      lastBurst = now;
      scene.burst(e.clientX, e.clientY);
    };
    document.addEventListener('click', onDown, { capture: true, passive: true });
    return () => {
      cancelled = true;
      scene?.stop();
      document.removeEventListener('click', onDown, { capture: true });
    };
  }, [season, scheme]);

  if (!season) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      <canvas ref={ref} className="block" />
    </div>
  );
}
